import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, User, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../data/site";
import { useStore } from "../context/StoreContext";
import { CartNavButton } from "./cart/CartWidget";

export const Nav = () => {
  const { customer, setAuthOpen } = useStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const solidHeader = pathname !== "/" || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  const openAccount = () => {
    setOpen(false);
    if (customer) navigate("/my-orders");
    else setAuthOpen(true);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${
          solidHeader ? "bg-[#1c120c]/95 shadow-lg shadow-black/20 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-8 lg:px-16">
          <button
            data-testid="nav-logo-luckys-kitchen"
            onClick={() => go("/")}
            aria-label="Lucky's Kitchen — back to top"
          >
            <Logo />
          </button>
          <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.to)}
                className="group relative text-[13px] font-semibold uppercase tracking-[0.16em] text-brand-cream/85 transition-colors hover:text-brand-gold"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-brand-gold transition-[width] duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              data-testid="nav-account-button"
              onClick={openAccount}
              className="flex items-center gap-2 rounded-full p-2 text-brand-cream transition-colors hover:text-brand-gold"
              aria-label={customer ? "My orders" : "Sign in"}
            >
              <User className="h-5 w-5" />
              <span className="hidden text-xs font-bold uppercase tracking-wider md:inline">
                {customer ? (customer.name || "Account").split(" ")[0] : "Sign In"}
              </span>
            </button>
            <CartNavButton />
            <button
              data-testid="nav-order-online-button"
              onClick={() => navigate("/order")}
              className="hidden rounded-full bg-brand-gold px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-brand-brown transition-transform duration-300 hover:scale-[1.05] sm:inline-flex"
            >ORDER ONLINE</button>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(true)}
              className="rounded-full p-2 text-brand-cream lg:hidden"
              aria-label="Open menu"
            >
              <MenuIcon className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#1A0E0B] px-6 py-5"
            data-testid="nav-mobile-drawer"
          >
            <div className="flex items-center justify-between">
              <Logo />
              <button
                data-testid="nav-mobile-close-button"
                onClick={() => setOpen(false)}
                className="rounded-full p-2 text-brand-cream"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="mt-14 flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4 }}
                  onClick={() => go(l.to)}
                  className="border-b border-brand-cream/10 py-4 text-left font-serif text-3xl font-semibold text-brand-cream transition-colors hover:text-brand-gold"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, x: -18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                onClick={openAccount}
                className="border-b border-brand-cream/10 py-4 text-left font-serif text-3xl font-semibold text-brand-gold"
                data-testid="nav-mobile-account-button"
              >
                {customer ? "My Orders" : "Sign In"}
              </motion.button>
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="mt-auto"
            >
              <button
                data-testid="nav-mobile-order-online-button"
                onClick={() => { setOpen(false); navigate("/order"); }}
                className="inline-flex w-full items-center justify-center rounded-full bg-brand-gold px-6 py-4 text-base font-bold uppercase tracking-wider text-brand-brown"
              >ORDER ONLINE</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
